import { LitElement, html, css, nothing } from 'lit';
import { unsafeSVG } from 'lit/directives/unsafe-svg.js';
import { Upload, CheckCircle2, Plus, Trash2, Download, RefreshCw, PenLine, Check, Paperclip } from 'lucide';
import { lucideIcon } from '../icon.js';
import { api } from '../api.js';
import { confirmDialog, alertDialog } from '../utils/confirm-dialog.js';
import { fieldOrderDocumentToText, FIELD_ORDER_ACCEPT } from '../utils/veldwerkopdracht/document-tekst.js';
import { kanScanZijn, teWeinigTekst, tekstUitScan, SCAN_VRAAG } from '../utils/veldwerkopdracht/scan-ocr.js';
import { controleerScan } from '../utils/veldwerkopdracht/scan-controle.js';
import { FIELD_PROTOCOLS, NEN5740_CODES, formatPlannedDates, normalizeFieldOrder, checkFieldOrder, summarizeFieldWork, checkAttachment, ATTACHMENT_ACCEPT, ATTACHMENT_HINT } from '@mv/models';
import { formatDateTime } from '../utils/datetime.js';

const clone = (o) => JSON.parse(JSON.stringify(o));

// ── De secties: één beschrijving voor de leesweergave én het bewerken ────────
// kind: text | long (meerregelig) | chips (lijst als labels) | lines (lijst als regels) | bool |
// person | rows | protocols | strategies | dates (van–tot). `full` = over de hele breedte. `also` =
// extra paden die de BRL-toets voor dit veld gebruikt.
const T = (path, label, o = {}) => ({ kind: 'text', path, label, ...o });
const BORING_COLS = [
  { key: 'count', label: 'Aantal', number: true, width: '80px' },
  { key: 'depthM', label: 'Diepte (m-mv)', number: true, width: '110px', unit: 'm' },
  { key: 'note', label: 'Opmerking' },
];
const WELL_COLS = [
  { key: 'count', label: 'Aantal', number: true, width: '58px' },
  { key: 'depthM', label: 'Diepte', number: true, width: '62px', unit: 'm' },
  { key: 'filterFromM', label: 'Filter van', number: true, width: '72px', unit: 'm' },
  { key: 'filterToM', label: 'tot', number: true, width: '62px', unit: 'm' },
  { key: 'type', label: 'Type', width: '90px' },
  { key: 'note', label: 'Opmerking' },
];
const PIT_COLS = [
  { key: 'kind', label: 'Soort', options: [['gat', 'gat'], ['sleuf', 'sleuf']], width: '90px' },
  { key: 'count', label: 'Aantal', number: true, width: '70px' },
  { key: 'depthM', label: 'Diepte', number: true, width: '70px', unit: 'm' },
  { key: 'dimensions', label: 'Afmetingen', width: '120px' },
  { key: 'note', label: 'Opmerking' },
];

const SECTIONS = [
  { id: 'project', title: 'Project', col: 1, fields: [
    T('project.number', 'Projectnummer'), T('project.name', 'Projectnaam'),
    T('project.address', 'Adres / locatie'), T('project.city', 'Plaats'),
    T('project.client', 'Opdrachtgever'), { kind: 'dates', path: 'plannedDate', label: 'Datum uitvoering', also: ['plannedDateEnd', 'plannedDateText'] },
    T('access', 'Toegang en afspraken op locatie', { kind: 'long', full: true }),
  ] },
  { id: 'contact', title: 'Contact', col: 1, fields: [
    { kind: 'person', path: 'projectLeader', label: 'Projectleider' },
    { kind: 'person', path: 'siteContact', label: 'Contactpersoon op locatie' },
    { kind: 'person', path: 'projectLeader2', label: 'Tweede projectleider / vervanger' },
  ] },
  { id: 'onderzoek', title: 'Onderzoek', col: 1, fields: [
    T('investigation.type', 'Soort onderzoek'), T('investigation.strategy', 'Strategie', { placeholder: 'NEN 5740, NEN 5707, …' }),
    { kind: 'strategies', path: 'investigation.strategyCodes', label: 'Strategieën NEN 5740', full: true },
    T('investigation.suspicion', 'Verdachtheid / hypothese'), T('investigation.area', 'Oppervlakte'),
    T('investigation.expectedSoil', 'Verwachte grondsoort'), T('investigation.groundwaterFlow', 'Stromingsrichting grondwater'),
    T('investigation.reason', 'Aanleiding', { kind: 'long', full: true }),
    T('investigation.goal', 'Doel', { kind: 'long', full: true }),
    { kind: 'chips', path: 'investigation.expectedContaminants', label: 'Verwachte verontreinigingen' },
    { kind: 'chips', path: 'investigation.pavements', label: 'Verharding' },
  ] },
  { id: 'protocollen', title: 'Protocollen (BRL SIKB 2000)', nav: 'Protocollen', col: 1, fields: [
    { kind: 'protocols', path: 'protocols', label: 'Protocollen', full: true },
  ] },
  { id: 'veiligheid', title: 'Veiligheid', col: 1, fields: [
    T('safety.safetyClass', 'Veiligheidsklasse'), { kind: 'bool', path: 'safety.klic', label: 'KLIC-melding' },
    T('safety.cablesAndPipes', 'Kabels en leidingen'), T('safety.trafficMeasures', 'Verkeersmaatregelen'),
    { kind: 'chips', path: 'safety.ppe', label: 'Persoonlijke Beschermingsmiddelen (PBM)', full: true },
    { kind: 'chips', path: 'safety.risks', label: 'Risico\'s (TRA)', full: true },
    T('safety.otherMeasures', 'Overige maatregelen', { kind: 'long', full: true }),
  ] },
  { id: 'werk', title: 'Werkzaamheden', col: 2, fields: [
    { kind: 'rows', path: 'work.borings', label: 'Boringen', cols: BORING_COLS, blank: { count: null, depthM: null, note: null }, full: true },
    { kind: 'rows', path: 'work.wells', label: 'Peilbuizen', cols: WELL_COLS, blank: { count: null, depthM: null, filterFromM: null, filterToM: null, type: null, note: null }, full: true },
    { kind: 'rows', path: 'work.pits', label: 'Inspectiegaten en sleuven', cols: PIT_COLS, blank: { kind: 'gat', count: null, depthM: null, dimensions: null, note: null }, full: true },
    T('work.hardSurface', 'Asfalt / beton', { full: true }), T('work.machinery', 'Machine / boorsysteem', { full: true }),
    { kind: 'chips', path: 'work.equipment', label: 'Benodigdheden', full: true },
  ] },
  { id: 'monsters', title: 'Monsterneming', col: 2, fields: [
    { kind: 'lines', path: 'sampling.soil', label: 'Grondmonsters', full: true },
    T('sampling.groundwater', 'Grondwater', { kind: 'long' }), T('sampling.asbestos', 'Asbest', { kind: 'long' }),
  ] },
  { id: 'inmeten', title: 'Inmeten', col: 2, fields: [
    T('surveying.method', 'Methode', { placeholder: 'GPS, dGPS, t.o.v. vast punt' }), { kind: 'bool', path: 'surveying.heightNap', label: 'NAP-hoogte inmeten' },
    { kind: 'bool', path: 'surveying.photos', label: 'Foto\'s maken' }, T('surveying.note', 'Opmerking'),
  ] },
  { id: 'lab', title: 'Laboratorium', col: 2, fields: [
    T('lab.name', 'Laboratorium'), T('lab.customerCode', 'Klantcode'),
    { kind: 'chips', path: 'lab.packages', label: 'Analysepakketten', full: true },
    T('lab.packaging', 'Monsterverpakking', { full: true }),
  ] },
  { id: 'afspraken', title: 'Afspraken', col: 2, fields: [
    { kind: 'lines', path: 'delivery', label: 'Inleveren', full: true },
    { kind: 'chips', path: 'attachments', label: 'Bijlagen genoemd in het formulier', full: true },
    { kind: 'lines', path: 'remarks', label: 'Opmerkingen voor de veldwerker', full: true },
  ] },
];

/** Hoort een BRL-melding (veldpad) bij dit veld? */
const fieldCovers = (f, checkField) => [f.path, ...(f.also || [])]
  .some((p) => checkField === p || checkField.startsWith(`${p}.`));
const sectionOf = (checkField) => SECTIONS.find((s) => s.fields.some((f) => fieldCovers(f, checkField)));

/** Is een veld leeg (niet genoemd)? */
function isEmpty(f, v, data) {
  if (f.kind === 'dates') return !data?.plannedDate && !data?.plannedDateText;
  if (f.kind === 'person') return !v || !(v.name || v.company || v.phone || v.email);
  if (['chips', 'lines', 'rows', 'protocols', 'strategies'].includes(f.kind)) return !v || !v.length;
  if (f.kind === 'bool') return v == null;
  return v == null || String(v).trim() === '';
}

const nl = (n) => String(n).replace('.', ',');

/** Waarde op een pad ('safety.klic', 'work.borings.0.count') lezen of schrijven. */
function getPath(obj, path) {
  return path.split('.').reduce((o, k) => (o == null ? undefined : o[k]), obj);
}
function setPath(obj, path, value) {
  const keys = path.split('.');
  let o = obj;
  for (let i = 0; i < keys.length - 1; i++) o = o[keys[i]];
  o[keys[keys.length - 1]] = value;
}

/**
 * field-order-panel — de veldwerkopdracht van een project: inlezen, nakijken, goedkeuren.
 *
 * Het formulier van de projectleider (Excel, PDF, Word) wordt in de browser naar tekst
 * omgezet -- met de aangevinkte vakjes erin -- en de backend laat de AI het vaste schema
 * invullen. Wat er dan staat is een CONCEPT: alles is aan te passen, en bovenaan staat wat er
 * volgens BRL SIKB 2000 nog ontbreekt. Klik op zo'n regel en je springt naar het veld.
 *
 * Pas na goedkeuren ziet de veldwerker de opdracht in de app. Een nieuw formulier inlezen
 * maakt een nieuw concept; de vorige goedgekeurde blijft tot dan in de app staan.
 *
 * @prop projectId
 * @prop refreshSignal  verhogen = opnieuw ophalen (WS-gebeurtenis 'field-order-update')
 */
export class FieldOrderPanel extends LitElement {
  static properties = {
    projectId: { type: String },
    refreshSignal: { type: Number },
    _loading: { state: true },
    _order: { state: true },      // zoals van de server
    _approvedInfo: { state: true },
    _data: { state: true },       // lokale, bewerkbare kopie
    _dirty: { state: true },
    _busy: { state: true },
    _busyText: { state: true },
    _error: { state: true },
    _showAdvised: { state: true },
    _vast: { state: true },       // de kopbalk plakt bovenaan (er is langs gescrold)
    _editing: { state: true },    // Set met de secties die open staan om te bewerken
    _files: { state: true },      // geüploade bijlagen (per project)
    _uploading: { state: true },
    _fileError: { state: true },
  };

  constructor() {
    super();
    this._loading = true;
    this._order = null;
    this._data = null;
    this._dirty = false;
    this._busy = false;
    this._showAdvised = true;
    this._vast = false;
    this._editing = new Set();
    this._files = [];
    this._uploading = false;
    this._fileError = null;
    // Plakt de kopbalk? Dan staat het ankertje erboven al hoger dan de balk zelf. Op elk
    // scrollende element (capture), want de pagina scrolt in een binnencontainer.
    this._onScroll = () => {
      const anker = this.renderRoot?.querySelector('.bar-anker');
      const balk = this.renderRoot?.querySelector('.bar');
      const vast = !!(anker && balk) && anker.getBoundingClientRect().top < balk.getBoundingClientRect().top - 1;
      if (vast !== this._vast) this._vast = vast;
    };
  }

  connectedCallback() {
    super.connectedCallback();
    window.addEventListener('scroll', this._onScroll, { passive: true });
    window.addEventListener('resize', this._onScroll);
    this.updateComplete.then(() => this._koppelScrollers());
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this._onScroll);
    window.removeEventListener('resize', this._onScroll);
    for (const el of this._scrollers || []) el.removeEventListener('scroll', this._onScroll);
    this._scrollers = [];
    super.disconnectedCallback();
  }

  /**
   * De pagina scrolt in een container binnen de shadow DOM van de app. Een scroll-event komt
   * daar niet uit (het is niet 'composed'), dus een luisteraar op document hoort niets. Daarom
   * zelf omhoog lopen -- ook via de host van elke shadow root -- en op elke scrollende voorouder
   * luisteren.
   */
  _koppelScrollers() {
    for (const el of this._scrollers || []) el.removeEventListener('scroll', this._onScroll);
    this._scrollers = [];
    let el = this;
    while (el) {
      el = el.parentNode || el.host || null;
      if (el && el.nodeType === 1) {
        const oy = getComputedStyle(el).overflowY;
        if (oy === 'auto' || oy === 'scroll' || oy === 'overlay') {
          el.addEventListener('scroll', this._onScroll, { passive: true });
          this._scrollers.push(el);
        }
      }
    }
    this._onScroll();
  }

  updated(changed) {
    if ((changed.has('projectId') || changed.has('refreshSignal')) && this.projectId && !this._dirty) this._load();
  }

  async _load() {
    try {
      const [r, b] = await Promise.all([
        api.getFieldOrder(this.projectId),
        api.listFieldOrderAttachments(this.projectId).catch(() => ({ attachments: [] })),
      ]);
      this._setOrder(r.fieldOrder);
      this._approvedInfo = r.approved;
      this._files = b.attachments || [];
      this._error = null;
    } catch (e) {
      this._error = e.message;
    } finally {
      this._loading = false;
    }
  }

  _setOrder(order) {
    this._order = order;
    this._data = order ? clone(normalizeFieldOrder(order.data)) : null;
    this._dirty = false;
    // Een sectie zonder één ingevuld veld (zelf opstellen, of het formulier noemde het niet)
    // staat meteen open om in te vullen; de rest is eerst om te lezen.
    this._editing = new Set(this._data
      ? SECTIONS.filter((s) => s.fields.every((f) => isEmpty(f, getPath(this._data, f.path), this._data))).map((s) => s.id)
      : []);
  }

  /** Sectie openzetten of dichtdoen om te bewerken; zonder `open` wisselt hij. */
  _toggleEdit(id, open) {
    if (open === undefined) open = !this._editing.has(id);
    const e = new Set(this._editing);
    if (open) e.add(id); else e.delete(id);
    this._editing = e;
  }

  // ── Acties ─────────────────────────────────────────────────────────────────

  async _onFile(file) {
    if (!file) return;
    if (this._dirty && !await confirmDialog({ title: 'Wijzigingen weggooien?', message: 'Je hebt nog niet opgeslagen wijzigingen. Een nieuw formulier inlezen gooit die weg.', confirmLabel: 'Toch inlezen', danger: true })) return;
    this._busy = true;
    this._error = null;
    try {
      this._busyText = 'Formulier lezen…';
      let { text } = await fieldOrderDocumentToText(file);
      if (teWeinigTekst(text)) {
        // Een scan of foto: geen tekstlaag. Na toestemming laten herkennen (OCR).
        if (!kanScanZijn(file)) throw new Error('Er staat (bijna) geen tekst in dit bestand.');
        if (!await confirmDialog(SCAN_VRAAG(file.name))) return;
        const scan = await tekstUitScan(this.projectId, file, (t) => { this._busyText = t; });
        // Eerst laten zien wat er gelezen is; pas na akkoord wordt het een veldwerkopdracht.
        this._busy = false;
        text = await controleerScan({ text: scan.text, paginas: scan.paginas, bestandsnaam: file.name });
        if (text == null) return;
        this._busy = true;
      }
      this._busyText = 'Opdracht herkennen… (± 10 seconden)';
      const r = await api.extractFieldOrder(this.projectId, file, text);
      this._setOrder(r.fieldOrder);
      this.dispatchEvent(new CustomEvent('field-order-changed', { bubbles: true, composed: true }));
    } catch (e) {
      this._error = e.message;
    } finally {
      this._busy = false;
    }
  }

  /** Zelf opstellen: leeg concept met wat het project al weet; de BRL-toets wijst de rest aan. */
  async _createBlank() {
    if (this._dirty && !await confirmDialog({ title: 'Wijzigingen weggooien?', message: 'Je hebt nog niet opgeslagen wijzigingen.', confirmLabel: 'Toch beginnen', danger: true })) return;
    if (this._order && !this._dirty && !await confirmDialog({
      title: 'Nieuwe opdracht opstellen?',
      message: 'Er komt een nieuw, leeg concept. De huidige opdracht blijft bewaard en een goedgekeurde blijft in de veldapp staan tot het nieuwe concept is goedgekeurd.',
      confirmLabel: 'Nieuw concept',
    })) return;
    this._busy = true;
    this._busyText = 'Concept aanmaken…';
    try {
      const r = await api.createFieldOrder(this.projectId);
      this._setOrder(r.fieldOrder);
      this.dispatchEvent(new CustomEvent('field-order-changed', { bubbles: true, composed: true }));
    } catch (e) {
      this._error = e.message;
    } finally {
      this._busy = false;
    }
  }

  async _save() {
    this._busy = true;
    this._busyText = 'Opslaan…';
    try {
      const r = await api.updateFieldOrder(this._order.id, this._data);
      this._setOrder(r.fieldOrder);
    } catch (e) {
      this._error = e.message;
    } finally {
      this._busy = false;
    }
  }

  async _approve() {
    const required = checkFieldOrder(this._data).filter((c) => c.level === 'required');
    if (required.length) {
      const ok = await confirmDialog({
        title: `Goedkeuren met ${required.length} open ${required.length === 1 ? 'eis' : 'eisen'}?`,
        message: `Volgens BRL SIKB 2000 ontbreekt nog: ${required.slice(0, 5).map((c) => c.message.replace(/\.$/, '')).join('; ')}${required.length > 5 ? '; …' : ''}. Staat dit in een bijlage of is het mondeling afgesproken, dan kun je toch goedkeuren.`,
        confirmLabel: 'Toch goedkeuren',
      });
      if (!ok) return;
    }
    this._busy = true;
    this._busyText = 'Goedkeuren…';
    try {
      if (this._dirty) await api.updateFieldOrder(this._order.id, this._data);
      const r = await api.approveFieldOrder(this._order.id);
      this._setOrder(r.fieldOrder);
      this._approvedInfo = null;
      this.dispatchEvent(new CustomEvent('field-order-changed', { bubbles: true, composed: true }));
    } catch (e) {
      this._error = e.message;
    } finally {
      this._busy = false;
    }
  }

  async _delete() {
    const ok = await confirmDialog({
      title: 'Veldwerkopdracht verwijderen?',
      message: this._order.status === 'approved'
        ? 'De opdracht verdwijnt ook uit de veldapp.'
        : 'Dit concept verdwijnt. Een eerder goedgekeurde opdracht blijft in de veldapp staan.',
      confirmLabel: 'Verwijderen', danger: true,
    });
    if (!ok) return;
    try {
      await api.deleteFieldOrder(this._order.id);
      this._order = null;
      this._data = null;
      await this._load();
      this.dispatchEvent(new CustomEvent('field-order-changed', { bubbles: true, composed: true }));
    } catch (e) {
      this._error = e.message;
    }
  }

  async _downloadSource() {
    try {
      const blob = await api.fetchFieldOrderSource(this._order.id);
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = this._order.sourceFilename || 'veldwerkopdracht';
      a.click();
      setTimeout(() => URL.revokeObjectURL(a.href), 2000);
    } catch (e) {
      alertDialog({ title: 'Downloaden mislukt', message: e.message });
    }
  }

  // ── Bijlagen ───────────────────────────────────────────────────────────────

  async _uploadFiles(fileList) {
    const files = [...(fileList || [])];
    if (!files.length) return;
    this._fileError = null;
    // Eerst in de browser op type en grootte; de backend controleert daarna ook de inhoud.
    const geweigerd = files.map((f) => ({ f, c: checkAttachment(f.name, f.size) })).filter((x) => !x.c.ok);
    const goed = files.filter((f) => checkAttachment(f.name, f.size).ok);
    const fouten = geweigerd.map((x) => `${x.f.name}: ${x.c.error}`);
    this._uploading = true;
    for (const f of goed) {
      try {
        const r = await api.uploadFieldOrderAttachment(this.projectId, f);
        this._files = [...this._files, r.attachment];
      } catch (e) {
        fouten.push(`${f.name}: ${e.message}`);
      }
    }
    this._uploading = false;
    if (fouten.length) this._fileError = fouten.join('\n');
  }

  async _downloadFile(a) {
    try {
      const blob = await api.fetchFieldOrderAttachment(a.id);
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = a.filename;
      link.click();
      setTimeout(() => URL.revokeObjectURL(link.href), 2000);
    } catch (e) {
      alertDialog({ title: 'Downloaden mislukt', message: e.message });
    }
  }

  async _deleteFile(a) {
    if (!await confirmDialog({ title: 'Bijlage verwijderen?', message: `${a.filename} wordt verwijderd.`, confirmLabel: 'Verwijderen', danger: true })) return;
    try {
      await api.deleteFieldOrderAttachment(a.id);
      this._files = this._files.filter((x) => x.id !== a.id);
    } catch (e) {
      this._fileError = e.message;
    }
  }

  _renderFiles() {
    const kb = (n) => (n >= 1024 * 1024 ? `${nl((n / 1024 / 1024).toFixed(1))} MB` : `${Math.max(1, Math.round(n / 1024))} kB`);
    const eis = (this._checks || []).some((c) => c.level === 'required' && c.field === 'attachments');
    return html`<div class="files" data-field="attachments-upload">
      <div class="lbl">Bijlagen (boorplan, KLIC, kaart)${eis ? html`<i class="rdot"></i>` : ''}</div>
      ${this._files.length ? html`<ul class="flist">${this._files.map((a) => html`<li>
        ${unsafeSVG(lucideIcon(Paperclip, 14))}
        <button class="fname" title="Downloaden" @click=${() => this._downloadFile(a)}>${a.filename}</button>
        <small>${kb(a.sizeBytes)}</small>
        <button class="icon" title="Downloaden" @click=${() => this._downloadFile(a)}>${unsafeSVG(lucideIcon(Download, 14))}</button>
        <button class="icon del" title="Verwijderen" @click=${() => this._deleteFile(a)}>${unsafeSVG(lucideIcon(Trash2, 14))}</button>
      </li>`)}</ul>` : ''}
      <label class="fdrop" title=${`Toegestaan: ${ATTACHMENT_HINT} (max. 25 MB)`}
          @dragover=${(e) => e.preventDefault()} @drop=${(e) => { e.preventDefault(); this._uploadFiles(e.dataTransfer.files); }}>
        ${this._uploading ? html`<span class="spin"></span> Uploaden…` : html`${unsafeSVG(lucideIcon(Plus, 12))} Bijlage toevoegen`}
        <input type="file" multiple hidden accept=${ATTACHMENT_ACCEPT} ?disabled=${this._uploading}
          @change=${(e) => { this._uploadFiles(e.target.files); e.target.value = ''; }}>
      </label>
      ${this._fileError ? html`<div class="ferr">${this._fileError}</div>` : ''}
      <small class="fhint">PDF, Word, Excel, PowerPoint, foto's, AutoCAD, KML en e-mail; max. 25 MB per bestand.</small>
    </div>`;
  }

  _set(path, value) {
    const d = clone(this._data);
    setPath(d, path, value);
    this._data = d;
    this._dirty = true;
  }

  async _jumpTo(field) {
    // Eerst de sectie openzetten om te bewerken, dan het dichtstbijzijnde veld:
    // 'work.borings.0.depthM' → dat veld, anders zijn groep.
    const sectie = sectionOf(field);
    if (sectie && !this._editing.has(sectie.id)) {
      this._toggleEdit(sectie.id, true);
      await this.updateComplete;
    }
    if (field === 'plannedDateText' || field === 'plannedDateEnd') field = 'plannedDate';
    if (field === 'attachments') field = 'attachments-upload';
    const parts = field.split('.');
    for (let n = parts.length; n > 0; n--) {
      const el = this.renderRoot.querySelector(`[data-field="${parts.slice(0, n).join('.')}"]`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        const input = el.matches('input,select,textarea') ? el : el.querySelector('input,select,textarea');
        input?.focus({ preventScroll: true });
        el.classList.add('flash');
        setTimeout(() => el.classList.remove('flash'), 1400);
        return;
      }
    }
  }

  // ── Veldjes ────────────────────────────────────────────────────────────────

  /** Rood markeren alleen bij een open eis; een advies blijft neutraal. */
  _flagged(path) {
    return (this._checks || []).some((c) => c.level === 'required' && (c.field === path || c.field.startsWith(`${path}.`))) ? 'flag' : '';
  }

  _text(path, label, { multiline = false, placeholder = '' } = {}) {
    const v = getPath(this._data, path) ?? '';
    return html`<label class="fld ${this._flagged(path)}" data-field=${path}><span>${label}</span>
      ${multiline
        ? html`<textarea rows="3" .value=${v} placeholder=${placeholder} @input=${(e) => this._set(path, e.target.value || null)}></textarea>`
        : html`<input .value=${v} placeholder=${placeholder} @input=${(e) => this._set(path, e.target.value || null)}>`}
    </label>`;
  }

  /**
   * Datum van uitvoering als van–tot. De tekst (plannedDateText) volgt de datums, zodat een
   * oudere veldapp die alleen de tekst toont hetzelfde laat zien.
   */
  _dates(label) {
    const d = this._data;
    const zet = (patch) => {
      const n = clone(d);
      Object.assign(n, patch);
      if (n.plannedDateEnd && (!n.plannedDate || n.plannedDateEnd <= n.plannedDate)) n.plannedDateEnd = null;
      n.plannedDateText = n.plannedDate ? formatPlannedDates(n) : null;
      this._data = n;
      this._dirty = true;
    };
    const oudeTekst = !d.plannedDate && d.plannedDateText;
    return html`<div class="fld ${this._flagged('plannedDate')}" data-field="plannedDate"><span>${label}</span>
      <div class="datums">
        <input type="date" aria-label="Van" .value=${d.plannedDate || ''} @change=${(e) => zet({ plannedDate: e.target.value || null })}>
        <span>tot en met</span>
        <input type="date" aria-label="Tot en met" .value=${d.plannedDateEnd || ''} min=${d.plannedDate || ''} ?disabled=${!d.plannedDate}
          @change=${(e) => zet({ plannedDateEnd: e.target.value || null })}>
      </div>
      ${oudeTekst ? html`<small class="hint">In de opdracht: ${d.plannedDateText}</small>` : ''}
    </div>`;
  }

  _list(path, label, placeholder = 'één per regel') {
    const v = (getPath(this._data, path) || []).join('\n');
    return html`<label class="fld ${this._flagged(path)}" data-field=${path}><span>${label}</span>
      <textarea rows=${Math.max(2, Math.min(8, (getPath(this._data, path) || []).length + 1))} .value=${v} placeholder=${placeholder}
        @input=${(e) => this._set(path, e.target.value.split('\n').map((s) => s.trim()).filter(Boolean))}></textarea>
    </label>`;
  }

  _bool(path, label) {
    const v = getPath(this._data, path);
    return html`<label class="fld ${this._flagged(path)}" data-field=${path}><span>${label}</span>
      <select @change=${(e) => this._set(path, e.target.value === '' ? null : e.target.value === 'ja')}>
        <option value="" ?selected=${v == null}>niet genoemd</option>
        <option value="ja" ?selected=${v === true}>ja</option>
        <option value="nee" ?selected=${v === false}>nee</option>
      </select>
    </label>`;
  }

  _person(path, label) {
    return html`<div class="group" data-field=${path}><div class="glabel">${label}</div>
      <div class="grid2">
        ${this._text(`${path}.name`, 'Naam')}${this._text(`${path}.company`, 'Bedrijf')}
        ${this._text(`${path}.phone`, 'Telefoon')}${this._text(`${path}.email`, 'E-mail')}
      </div></div>`;
  }

  /** Tabel met rijen (boringen, peilbuizen, gaten): toevoegen en weghalen. */
  _rows(path, label, columns, blank) {
    const rows = getPath(this._data, path) || [];
    const num = (v) => (v === '' ? null : Number(String(v).replace(',', '.')));
    return html`<div class="group ${this._flagged(path)}" data-field=${path}>
      <div class="glabel">${label}</div>
      ${rows.length ? html`
        <table class="rows"><thead><tr>${columns.map((c) => html`<th style=${c.width ? `width:${c.width}` : ''}>${c.label}</th>`)}<th></th></tr></thead>
        <tbody>${rows.map((r, i) => html`<tr>
          ${columns.map((c) => html`<td data-field=${`${path}.${i}.${c.key}`} class=${this._flagged(`${path}.${i}.${c.key}`)}>
            ${c.options
              ? html`<select @change=${(e) => this._set(`${path}.${i}.${c.key}`, e.target.value || null)}>
                  <option value="" ?selected=${!r[c.key]}>—</option>
                  ${c.options.map(([v, l]) => html`<option value=${v} ?selected=${r[c.key] === v}>${l}</option>`)}</select>`
              : html`<input .value=${r[c.key] ?? ''} inputmode=${c.number ? 'decimal' : 'text'}
                  @input=${(e) => this._set(`${path}.${i}.${c.key}`, c.number ? num(e.target.value) : (e.target.value || null))}>`}
          </td>`)}
          <td><button class="icon" title="Regel weghalen" @click=${() => { const d = clone(this._data); getPath(d, path).splice(i, 1); this._data = d; this._dirty = true; }}>${unsafeSVG(lucideIcon(Trash2, 14))}</button></td>
        </tr>`)}</tbody></table>` : html`<div class="empty-row">Geen</div>`}
      <button class="add" @click=${() => { const d = clone(this._data); getPath(d, path).push(clone(blank)); this._data = d; this._dirty = true; }}>
        ${unsafeSVG(lucideIcon(Plus, 13))} Regel toevoegen</button>
    </div>`;
  }

  // ── Weergave ───────────────────────────────────────────────────────────────

  _renderEmpty() {
    return html`
      <div class="intro">
        <h3>Veldwerkopdracht</h3>
        <p>Lees het opdrachtformulier van de projectleider in: Excel, PDF of Word, in welk sjabloon dan ook. Of stel de opdracht hier zelf op. Hij wordt getoetst aan BRL SIKB 2000, en na goedkeuren ziet de veldwerker hem in de app.</p>
        ${this._dropzone()}
        <div class="or">of</div>
        <button class="btn plain big" @click=${() => this._createBlank()}>${unsafeSVG(lucideIcon(PenLine, 15))} Zelf een opdracht opstellen</button>
      </div>`;
  }

  _dropzone(compact = false) {
    return html`<label class="drop ${compact ? 'compact' : ''}" @dragover=${(e) => e.preventDefault()}
        @drop=${(e) => { e.preventDefault(); this._onFile(e.dataTransfer.files[0]); }}>
      ${unsafeSVG(lucideIcon(compact ? RefreshCw : Upload, compact ? 14 : 24))}
      <b>${compact ? 'Nieuw formulier inlezen' : 'Sleep het formulier hierheen of klik om te kiezen'}</b>
      ${compact ? '' : html`<small>.xlsx, .xlsm, .pdf, .docx</small>`}
      <input type="file" hidden accept=${FIELD_ORDER_ACCEPT} @change=${(e) => { this._onFile(e.target.files[0]); e.target.value = ''; }}>
    </label>`;
  }

  _renderChecks() {
    const checks = this._checks;
    const required = checks.filter((c) => c.level === 'required');
    const advised = checks.filter((c) => c.level === 'advised');
    if (!checks.length) {
      return html`<div class="card checks ok">${unsafeSVG(lucideIcon(CheckCircle2, 16))} Voldoet aan de eisen van BRL SIKB 2000 voor de gekozen protocollen.</div>`;
    }
    const item = (c) => html`<li class=${c.level} @click=${() => this._jumpTo(c.field)}>
      <i class="dot"></i>
      <code>${c.protocol || (c.level === 'required' ? 'eis' : 'advies')}</code>
      <span class="msg">${c.message}</span>
      <span class="act">Invullen</span></li>`;
    return html`<div class="card checks">
      <div class="checks-head">
        <b>Toets BRL SIKB 2000</b>
        <span class="tel">${required.length ? html`<span class="req">${required.length} ${required.length === 1 ? 'eis' : 'eisen'} open</span>` : html`<span class="okt">eisen compleet</span>`}${advised.length ? html` · ${advised.length} ${advised.length === 1 ? 'advies' : 'adviezen'}` : ''}</span>
        ${advised.length ? html`<button class="link" @click=${() => { this._showAdvised = !this._showAdvised; }}>${this._showAdvised ? 'adviezen verbergen' : 'adviezen tonen'}</button>` : ''}
      </div>
      <ul>${required.map(item)}${this._showAdvised ? advised.map(item) : ''}</ul>
    </div>`;
  }

  // ── Leesweergave ───────────────────────────────────────────────────────────

  _readValue(f, v) {
    switch (f.kind) {
      case 'long': return html`<div class="val long">${v}</div>`;
      case 'bool': return html`<div class="val">${v ? 'Ja' : 'Nee'}</div>`;
      case 'strategies': return html`<div class="chips">${v.map((c) => html`<span class="chip"><b>${c}</b> ${NEN5740_CODES.find((x) => x.code === c)?.label || ''}</span>`)}</div>`;
      case 'dates': return html`<div class="val">${formatPlannedDates(this._data)}</div>`;
      case 'chips': return html`<div class="chips">${v.map((x) => html`<span class="chip">${x}</span>`)}</div>`;
      case 'lines': return html`<div class="val long">${v.map((x) => html`<div>${x}</div>`)}</div>`;
      case 'person': return html`<div class="val">
        <b>${v.name || '—'}</b>
        ${v.company ? html`<div>${v.company}</div>` : ''}
        ${v.phone || v.email ? html`<div>${[v.phone, v.email].filter(Boolean).join(' · ')}</div>` : ''}</div>`;
      case 'rows': return html`<table class="rtab"><thead><tr>${f.cols.map((c) => html`<th>${c.label}</th>`)}</tr></thead>
        <tbody>${v.map((r, i) => html`<tr>${f.cols.map((c) => {
          const x = r[c.key];
          const flag = this._flagged(`${f.path}.${i}.${c.key}`) || (c.key.startsWith('filter') && this._flagged(`${f.path}.${i}.filter`));
          return html`<td class=${flag ? 'flag' : ''}>${x == null || x === '' ? (flag ? '?' : '—') : c.number ? `${nl(x)}${c.unit ? ` ${c.unit}` : ''}` : x}</td>`;
        })}</tr>`)}</tbody></table>`;
      case 'protocols': return html`<div class="protos">${FIELD_PROTOCOLS.map((p) => {
        const on = v.includes(p.id);
        return html`<div class="proto ${on ? 'on' : ''}"><span class="box">${on ? unsafeSVG(lucideIcon(Check, 12)) : ''}</span>
          <code>${p.id}</code><span>${p.label.replace(/^Protocol \d+ — /, '').replace(/^./, (c) => c.toUpperCase())}</span></div>`;
      })}</div>`;
      default: return html`<div class="val">${v}</div>`;
    }
  }

  _renderSectionRead(sec) {
    const filled = [];
    const missing = [];   // leeg én door de BRL-toets gemarkeerd: zichtbaar laten
    const empty = [];
    for (const f of sec.fields) {
      const v = getPath(this._data, f.path);
      const flag = (this._checks || []).some((c) => fieldCovers(f, c.field));
      if (f.kind === 'protocols' || !isEmpty(f, v, this._data)) filled.push(f);
      else if (flag && f.path !== 'attachments') missing.push(f);   // bijlagen: zie het uploadblok
      else empty.push(f);
    }
    const isRequired = (f) => (this._checks || []).some((c) => c.level === 'required' && fieldCovers(f, c.field));
    const flagDot = (f) => (isRequired(f) ? html`<i class="rdot"></i>` : '');
    return html`
      <div class="rgrid">
        ${filled.map((f) => html`<div class="rf ${f.full || ['long', 'rows', 'protocols'].includes(f.kind) ? 'full' : ''}">
          ${f.kind === 'protocols' ? '' : html`<div class="lbl">${f.label}${flagDot(f)}</div>`}
          ${this._readValue(f, getPath(this._data, f.path))}</div>`)}
        ${missing.map((f) => html`<div class="rf ${f.full ? 'full' : ''}"><div class="lbl">${f.label}${flagDot(f)}</div>
          <button class="toevoegen ${isRequired(f) ? 'req' : ''}" @click=${() => this._jumpTo(f.path)}>${unsafeSVG(lucideIcon(Plus, 12))} Toevoegen</button></div>`)}
      </div>
      ${empty.length ? html`<button class="leeg" @click=${() => this._toggleEdit(sec.id, true)}>
        + ${empty.length} ${empty.length === 1 ? 'leeg veld' : 'lege velden'}: ${empty.map((f) => f.label.toLowerCase()).join(', ')}</button>` : ''}`;
  }

  // ── Bewerken ───────────────────────────────────────────────────────────────

  _renderSectionEdit(sec) {
    const d = this._data;
    const veld = (f) => {
      switch (f.kind) {
        case 'long': return this._text(f.path, f.label, { multiline: true, placeholder: f.placeholder });
        case 'chips': case 'lines': return this._list(f.path, f.label);
        case 'dates': return this._dates(f.label);
        case 'strategies': return html`<div class="fld" data-field=${f.path}><span>${f.label}</span>
          <div class="chips">${NEN5740_CODES.map((c) => {
            const on = d.investigation.strategyCodes.includes(c.code);
            return html`<button type="button" class="chip kies ${on ? 'aan' : ''}" title=${c.label}
              @click=${() => this._set(f.path, on ? d.investigation.strategyCodes.filter((x) => x !== c.code)
                : NEN5740_CODES.map((x) => x.code).filter((x) => x === c.code || d.investigation.strategyCodes.includes(x)))}>${c.code}</button>`;
          })}</div></div>`;
        case 'bool': return this._bool(f.path, f.label);
        case 'person': return this._person(f.path, f.label);
        case 'rows': return this._rows(f.path, f.label, f.cols, f.blank);
        case 'protocols': return html`<div class="protocols ${this._flagged('protocols')}" data-field="protocols">
          ${FIELD_PROTOCOLS.map((p) => html`<label class="check">
            <input type="checkbox" .checked=${d.protocols.includes(p.id)}
              @change=${(e) => this._set('protocols', e.target.checked ? [...d.protocols, p.id].sort() : d.protocols.filter((x) => x !== p.id))}>
            ${p.label}</label>`)}</div>`;
        default: return this._text(f.path, f.label, { placeholder: f.placeholder });
      }
    };
    return html`<div class="egrid">${sec.fields.map((f) => html`<div class=${f.full || ['person', 'rows', 'dates'].includes(f.kind) ? 'full' : ''}>${veld(f)}</div>`)}</div>`;
  }

  _renderSection(sec) {
    const editing = this._editing.has(sec.id);
    // Een sectie zonder 'Toevoegen'-knop (protocollen) toont een open eis met een rode rand.
    const openReq = sec.fields.some((f) => f.kind === 'protocols' && (this._checks || []).some((c) => c.level === 'required' && fieldCovers(f, c.field)));
    return html`<section class="card ${openReq ? 'eis-open' : ''}" id=${`sec-${sec.id}`}>
      <div class="sec-head">
        <h4>${sec.title}</h4>
        <button class="btn plain small" @click=${() => this._toggleEdit(sec.id)}>${editing ? 'Klaar' : 'Bewerken'}</button>
      </div>
      ${editing ? this._renderSectionEdit(sec) : this._renderSectionRead(sec)}
      ${sec.id === 'afspraken' ? this._renderFiles() : ''}
    </section>`;
  }

  _renderNav() {
    return html`<nav class="card snav">
      ${SECTIONS.map((sec) => {
        const open = this._checks.filter((c) => c.level === 'required' && sectionOf(c.field) === sec).length;
        return html`<button @click=${() => this.renderRoot.getElementById(`sec-${sec.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' })}>
          <span>${sec.nav || sec.title}</span>
          ${open ? html`<b class="open">${open}</b>` : html`<span class="ok">${unsafeSVG(lucideIcon(Check, 14))}</span>`}
        </button>`;
      })}
    </nav>`;
  }

  _renderForm() {
    const d = this._data;
    const o = this._order;
    const summary = summarizeFieldWork(d);
    return html`
      <div class="bar-anker"></div>
      <div class="bar ${this._vast ? 'vast' : ''}">
        <div class="bar-left">
          <span class="status ${o.status}">${o.status === 'approved' ? 'Goedgekeurd' : 'Concept'}</span>
          <div class="bar-text">
            <b>${d.project.name || d.project.address || 'Veldwerkopdracht'}</b>
            <small>${d.project.number ? `Projectnr. ${d.project.number} · ` : ''}
              ${o.status === 'approved'
                ? `goedgekeurd ${o.approvedAt ? formatDateTime(o.approvedAt) : ''}${o.approvedByEmail ? ` door ${o.approvedByEmail}` : ''} · zichtbaar in de veldapp`
                : `${o.sourceFilename ? 'ingelezen' : 'opgesteld'} ${o.createdAt ? formatDateTime(o.createdAt) : ''} · nog niet zichtbaar in de veldapp${this._approvedInfo ? ' (daar staat nog de vorige goedgekeurde)' : ''}`}
              ${summary ? ` · ${summary}` : ''}
            </small>
          </div>
        </div>
        <div class="bar-right">
          ${o.sourceFilename ? html`<button class="btn plain" title="Origineel formulier" @click=${() => this._downloadSource()}>${unsafeSVG(lucideIcon(Download, 14))} ${o.sourceFilename}</button>` : ''}
          ${this._dropzone(true)}
          <button class="btn plain" title="Nieuwe opdracht zelf opstellen" @click=${() => this._createBlank()}>${unsafeSVG(lucideIcon(PenLine, 14))} Zelf opstellen</button>
          <button class="btn plain danger" title="Verwijderen" @click=${() => this._delete()}>${unsafeSVG(lucideIcon(Trash2, 14))}</button>
          ${this._dirty ? html`<button class="btn plain" ?disabled=${this._busy} @click=${() => this._save()}>Opslaan</button>` : ''}
          ${o.status !== 'approved' || this._dirty ? html`<button class="btn primary" ?disabled=${this._busy} @click=${() => (o.status === 'approved' ? this._save() : this._approve())}>
            ${o.status === 'approved' ? 'Wijzigingen opslaan' : 'Goedkeuren'}</button>` : ''}
        </div>
      </div>

      <div class="layout">
        ${this._renderNav()}
        <div class="main">
          ${this._renderChecks()}
          <div class="cols">
            <div class="col">${SECTIONS.filter((x) => x.col === 1).map((x) => this._renderSection(x))}</div>
            <div class="col">${SECTIONS.filter((x) => x.col === 2).map((x) => this._renderSection(x))}</div>
          </div>
        </div>
      </div>`;
  }

  render() {
    if (this._loading) return html`<div class="loading">Laden…</div>`;
    // Geüploade bijlagen tellen mee voor de toets (bv. protocol 2018: een kaart of boorplan).
    this._checks = this._data
      ? checkFieldOrder({ ...this._data, attachments: [...this._data.attachments, ...this._files.map((f) => f.filename)] })
      : [];
    return html`
      ${this._error ? html`<div class="err">${this._error}</div>` : ''}
      ${this._busy ? html`<div class="busy"><span class="spin"></span>${this._busyText}</div>` : ''}
      ${this._data ? this._renderForm() : this._renderEmpty()}`;
  }

  static styles = css`
    :host { display: block; }
    .loading { padding: 2rem; color: var(--mv-text-muted); }
    .err { margin-bottom: 12px; padding: 9px 12px; border-radius: 8px; font-size: .84rem; color: var(--mv-danger, #B91C1C); background: var(--mv-danger-bg, #FEE2E2); }
    .busy { display: flex; align-items: center; gap: 10px; margin-bottom: 12px; padding: 10px 12px; border-radius: 8px; font-size: .85rem;
      background: var(--mv-primary-tint, rgba(46,158,91,.08)); color: var(--mv-text); }
    .spin { width: 14px; height: 14px; border-radius: 50%; border: 2px solid var(--mv-primary); border-right-color: transparent; animation: s .8s linear infinite; }
    @keyframes s { to { transform: rotate(360deg); } }
    .intro { max-width: 640px; margin: 1.5rem auto; text-align: center; }
    .intro h3 { margin: 0 0 6px; font-size: 1.1rem; color: var(--mv-text); }
    .intro p { margin: 0 0 18px; font-size: .88rem; line-height: 1.5; color: var(--mv-text-muted); }
    .drop { display: flex; flex-direction: column; align-items: center; gap: 8px; padding: 34px 20px; border: 2px dashed var(--mv-border);
      border-radius: 12px; color: var(--mv-text-muted); cursor: pointer; font-size: .88rem; background: var(--mv-surface); }
    .drop:hover { border-color: var(--mv-primary); color: var(--mv-primary); }
    .drop b { color: var(--mv-text); font-weight: 600; }
    .drop.compact { flex-direction: row; padding: 6px 11px; border-width: 1px; border-style: solid; border-radius: 8px; font-size: .8rem; gap: 6px; }
    .drop.compact b { font-weight: 600; }
    .bar { display: flex; align-items: center; justify-content: space-between; gap: 14px; flex-wrap: wrap; padding: 12px 16px; margin-bottom: 12px;
      background: var(--mv-surface); border: 1px solid var(--mv-border); border-radius: 12px; position: sticky; top: 0; z-index: 5;
      transition: background .15s, box-shadow .15s, border-color .15s; }
    /* Vastgeplakt boven de content: getinte achtergrond en een schaduw, zodat hij zich onderscheidt. */
    .bar.vast { background: color-mix(in srgb, var(--mv-surface-alt, #f8f9fa) 94%, #64748b); border-color: var(--mv-border, #e2e4ea);
      box-shadow: 0 8px 20px rgba(15, 23, 42, .14); }
    .bar-anker { height: 0; }
    .bar-left { display: flex; align-items: center; gap: 12px; min-width: 0; }
    .bar-text { display: flex; flex-direction: column; min-width: 0; }
    .bar-text b { font-size: .95rem; color: var(--mv-text); }
    .bar-text small { font-size: .76rem; color: var(--mv-text-muted); }
    .bar-right { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
    .status { font-size: .72rem; font-weight: 800; text-transform: uppercase; letter-spacing: .04em; padding: 4px 9px; border-radius: 999px; white-space: nowrap; }
    .status.draft { background: #FEF3C7; color: #92400E; }
    .status.approved { background: var(--mv-primary-tint, #e9f5e6); color: var(--mv-primary, #2e9e5b); }
    .btn { display: inline-flex; align-items: center; gap: 6px; border-radius: 8px; padding: 7px 13px; font: 700 .8rem inherit; font-family: inherit; cursor: pointer; border: 1px solid var(--mv-border); }
    .btn.plain { background: var(--mv-surface); color: var(--mv-text); }
    .btn.plain.danger:hover { color: #b91c1c; border-color: #fca5a5; }
    .btn.primary { background: var(--mv-primary); border-color: var(--mv-primary); color: #fff; }
    .btn:disabled { opacity: .5; cursor: default; }
    .btn.big { padding: 10px 18px; font-size: .86rem; }
    .or { margin: 14px 0 10px; font-size: .8rem; color: var(--mv-text-muted); }
    .card { background: var(--mv-surface); border: 1px solid var(--mv-border); border-radius: 12px; }
    .layout { display: grid; grid-template-columns: 210px minmax(0, 1fr); gap: 14px; align-items: start; }
    .main { display: flex; flex-direction: column; gap: 14px; min-width: 0; }
    .cols { display: grid; grid-template-columns: minmax(0, 1fr) minmax(0, 1fr); gap: 14px; align-items: start; }
    .col { display: flex; flex-direction: column; gap: 14px; min-width: 0; }
    @media (max-width: 1180px) { .cols { grid-template-columns: minmax(0, 1fr); } }
    @media (max-width: 900px) { .layout { grid-template-columns: minmax(0, 1fr); } .snav { display: none; } }

    /* Sectie-navigatie links */
    .snav { position: sticky; top: 84px; padding: 6px; display: flex; flex-direction: column; }
    .snav button { display: flex; align-items: center; justify-content: space-between; gap: 8px; padding: 8px 10px; border: none; background: none;
      border-radius: 8px; font: inherit; font-size: .84rem; font-weight: 600; color: var(--mv-text); cursor: pointer; text-align: left; }
    .snav button:hover { background: var(--mv-surface-alt, #f3f4f6); }
    .snav .ok { display: inline-flex; color: var(--mv-primary); }
    .snav .open { font-size: .74rem; font-weight: 800; color: var(--mv-danger, #B91C1C); }

    /* BRL-toets */
    .checks { padding: 12px 16px; }
    .checks.ok { display: flex; align-items: center; gap: 8px; color: var(--mv-primary); font-size: .86rem; font-weight: 600; }
    .checks-head { display: flex; align-items: baseline; gap: 10px; margin-bottom: 4px; font-size: .86rem; color: var(--mv-text); }
    .tel { font-size: .78rem; font-weight: 600; color: #b45309; }
    .tel .req { color: var(--mv-danger, #B91C1C); }
    .tel .okt { color: var(--mv-primary); }
    .checks-head .link { margin-left: auto; }
    .link { border: none; background: none; padding: 0; font: inherit; font-size: .76rem; color: var(--mv-text-muted); cursor: pointer; text-decoration: underline; }
    .checks ul { list-style: none; margin: 0; padding: 0; }
    .checks li { display: flex; align-items: center; gap: 12px; padding: 8px 4px; border-top: 1px solid var(--mv-border); font-size: .84rem; cursor: pointer; color: var(--mv-text); }
    .checks li:first-child { border-top: none; }
    .checks li:hover .act { text-decoration: underline; }
    .dot { width: 7px; height: 7px; border-radius: 50%; flex-shrink: 0; background: #f59e0b; }
    li.required .dot { background: var(--mv-danger, #DC2626); }
    .checks code { width: 46px; flex-shrink: 0; font-size: .72rem; color: var(--mv-text-muted); }
    .msg { flex: 1; min-width: 0; }
    .act { flex-shrink: 0; font-size: .78rem; font-weight: 600; color: var(--mv-primary); }

    /* Secties */
    section { padding: 14px 16px; display: flex; flex-direction: column; gap: 12px; min-width: 0; scroll-margin-top: 84px; }
    .sec-head { display: flex; align-items: center; justify-content: space-between; gap: 10px; }
    section h4 { margin: 0; font-size: .95rem; font-weight: 800; color: var(--mv-text); }
    .btn.small { padding: 5px 11px; font-size: .76rem; font-weight: 600; }
    .rgrid { display: grid; grid-template-columns: minmax(0, 1fr) minmax(0, 1fr); gap: 14px 18px; }
    .rf { min-width: 0; display: flex; flex-direction: column; gap: 3px; }
    .rf.full, .egrid > .full { grid-column: 1 / -1; }
    .lbl { font-size: .72rem; font-weight: 600; color: var(--mv-text-muted); display: flex; align-items: center; gap: 5px; }
    .rdot { width: 6px; height: 6px; border-radius: 50%; background: var(--mv-danger, #DC2626); }
    .val { font-size: .88rem; line-height: 1.45; color: var(--mv-text); overflow-wrap: anywhere; }
    .val.long { white-space: pre-line; }
    .val b { font-weight: 700; }
    .chips { display: flex; flex-wrap: wrap; gap: 6px; }
    .datums { display: flex; align-items: center; gap: 8px; }
    .datums input { flex: 0 1 180px; min-width: 0; }
    .datums span { font-size: .8rem; color: var(--mv-text-muted); white-space: nowrap; }
    .hint { font-size: .75rem; color: var(--mv-text-muted); }
    .chip.kies { cursor: pointer; font: inherit; font-size: .8rem; font-weight: 600; color: var(--mv-text-muted); }
    .chip.kies.aan { background: var(--mv-primary); border-color: var(--mv-primary); color: #fff; }
    .chip { padding: 3px 10px; border: 1px solid var(--mv-border); border-radius: 999px; font-size: .8rem; color: var(--mv-text); background: var(--mv-surface); }
    .rtab { width: 100%; border-collapse: separate; border-spacing: 0; border: 1px solid var(--mv-border); border-radius: 8px; overflow: hidden; font-size: .85rem; }
    .rtab th { text-align: left; font-size: .68rem; font-weight: 700; letter-spacing: .04em; text-transform: uppercase; color: var(--mv-text-muted);
      padding: 7px 10px; background: var(--mv-surface-alt, #f8f9fa); }
    .rtab td { padding: 7px 10px; border-top: 1px solid var(--mv-border); color: var(--mv-text); }
    .rtab td:first-child { font-weight: 700; }
    .rtab td.flag { color: var(--mv-danger, #DC2626); font-weight: 700; }
    .protos { display: flex; flex-direction: column; gap: 9px; }
    .proto { display: flex; align-items: center; gap: 10px; font-size: .86rem; color: var(--mv-text-muted); }
    .proto.on { color: var(--mv-text); }
    .proto code { font-weight: 700; font-size: .8rem; }
    .box { width: 17px; height: 17px; border-radius: 4px; border: 1.5px solid var(--mv-border); display: inline-flex; align-items: center; justify-content: center; flex-shrink: 0; }
    .proto.on .box { background: var(--mv-primary); border-color: var(--mv-primary); color: #fff; }
    .leeg { align-self: flex-start; border: none; background: none; padding: 0; font: inherit; font-size: .78rem; color: var(--mv-text-muted); cursor: pointer; text-align: left; }
    .leeg:hover { color: var(--mv-primary); }
    .toevoegen { align-self: flex-start; display: inline-flex; align-items: center; gap: 4px; padding: 3px 9px; border: 1px dashed var(--mv-border);
      border-radius: 6px; background: var(--mv-surface-alt, #f8f9fa); color: var(--mv-text-muted); font: inherit; font-size: .78rem; font-weight: 600; cursor: pointer; }
    .toevoegen:hover { color: var(--mv-text); }
    .toevoegen.req { border-color: var(--mv-danger, #DC2626); background: var(--mv-danger-bg, #FEF2F2); color: var(--mv-danger, #B91C1C); }
    .card.eis-open { border-color: #fca5a5; box-shadow: 0 0 0 1px #fca5a5; }
    .files { display: flex; flex-direction: column; gap: 6px; border-radius: 8px; }
    .flist { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; border: 1px solid var(--mv-border); border-radius: 8px; }
    .flist li { display: flex; align-items: center; gap: 8px; padding: 6px 8px 6px 10px; border-top: 1px solid var(--mv-border); font-size: .85rem; color: var(--mv-text-muted); }
    .flist li:first-child { border-top: none; }
    .fname { flex: 1; min-width: 0; text-align: left; border: none; background: none; padding: 0; font: inherit; color: var(--mv-text); cursor: pointer;
      overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
    .fname:hover { color: var(--mv-primary); text-decoration: underline; }
    .flist small { font-size: .74rem; white-space: nowrap; }
    .flist .icon:hover { color: var(--mv-primary); }
    .flist .icon.del:hover { color: #b91c1c; }
    /* Zelfde uiterlijk als de grijze 'Toevoegen'-knop. */
    .fdrop { align-self: flex-start; display: inline-flex; align-items: center; gap: 4px; padding: 3px 9px; border: 1px dashed var(--mv-border); border-radius: 6px;
      font-size: .78rem; font-weight: 600; color: var(--mv-text-muted); cursor: pointer; background: var(--mv-surface-alt, #f8f9fa); }
    .fdrop:hover { color: var(--mv-text); }
    .fdrop .spin { width: 12px; height: 12px; }
    .ferr { white-space: pre-line; font-size: .78rem; padding: 7px 10px; border-radius: 7px; color: var(--mv-danger, #B91C1C); background: var(--mv-danger-bg, #FEE2E2); }
    .fhint { font-size: .72rem; color: var(--mv-text-muted); }
    .egrid { display: grid; grid-template-columns: minmax(0, 1fr) minmax(0, 1fr); gap: 10px; }
    .egrid > div { min-width: 0; }
    .grid2 { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
    .fld { display: flex; flex-direction: column; gap: 4px; min-width: 0; border-radius: 8px; transition: box-shadow .3s; }
    .fld span { font-size: .74rem; font-weight: 600; color: var(--mv-text-sub, #4b5563); }
    .fld input, .fld select, .fld textarea, .rows input, .rows select { padding: 7px 9px; border: 1px solid var(--mv-border); border-radius: 7px; font: inherit; font-size: .84rem;
      background: var(--mv-surface); color: var(--mv-text); min-width: 0; width: 100%; box-sizing: border-box; }
    .fld textarea { resize: vertical; line-height: 1.4; }
    .fld input:focus, .fld textarea:focus, .fld select:focus, .rows input:focus { outline: none; border-color: var(--mv-primary); box-shadow: 0 0 0 3px var(--mv-primary-soft, rgba(34,139,76,.16)); }
    .flag > span::after, .group.flag > .glabel::after { content: ' •'; color: var(--mv-danger, #B91C1C); }
    td.flag input, .fld.flag input, .fld.flag select, .fld.flag textarea { border-color: #fca5a5; }
    .flash { box-shadow: 0 0 0 3px rgba(234,88,12,.45); }
    .group { display: flex; flex-direction: column; gap: 6px; }
    .glabel { font-size: .76rem; font-weight: 700; color: var(--mv-text); }
    /* Vaste kolommen houden hun breedte; Opmerking (zonder breedte) krijgt de rest van de kaart. */
    .rows { width: 100%; border-collapse: collapse; table-layout: fixed; }
    .rows th:last-child { width: 30px; }
    .rows th { text-align: left; font-size: .7rem; font-weight: 600; color: var(--mv-text-muted); padding: 0 4px 3px; }
    .rows td { padding: 2px 3px; }
    .empty-row { font-size: .8rem; color: var(--mv-text-muted); }
    .add { align-self: flex-start; display: inline-flex; align-items: center; gap: 5px; border: none; background: none; padding: 2px 0; font: 600 .78rem inherit;
      font-family: inherit; color: var(--mv-primary); cursor: pointer; }
    .icon { border: none; background: none; cursor: pointer; color: var(--mv-text-muted); display: inline-flex; padding: 4px; }
    .icon:hover { color: #b91c1c; }
    .protocols { display: flex; flex-direction: column; gap: 6px; }
    .check { display: flex; gap: 8px; align-items: center; font-size: .84rem; color: var(--mv-text); cursor: pointer; }
  `;
}

customElements.define('field-order-panel', FieldOrderPanel);
